/**
 * Superpeak – fiktiv kurv.
 *
 * Der sker ingen betaling her. Kurven er bare en måde at vise kunden
 * hvad de har valgt, og sende det med, når de udfylder bestillings-
 * formularen. Alt gemmes i localStorage, så den følger med på tværs
 * af sider uden login eller server-side session.
 */

const STORAGE_KEY = 'superpeak_cart';

function emptyCart() {
	return { plan: null, addons: [] };
}

function readCart() {
	try {
		const raw = localStorage.getItem( STORAGE_KEY );
		if ( ! raw ) {
			return emptyCart();
		}
		const parsed = JSON.parse( raw );
		return {
			plan: parsed.plan || null,
			addons: Array.isArray( parsed.addons ) ? parsed.addons : [],
		};
	} catch ( e ) {
		return emptyCart();
	}
}

function writeCart( cart ) {
	localStorage.setItem( STORAGE_KEY, JSON.stringify( cart ) );
	window.dispatchEvent( new CustomEvent( 'superpeak:cart-updated', { detail: cart } ) );
	return cart;
}

export function getCart() {
	return readCart();
}

/**
 * Sætter den valgte hostingplan. Der kan kun være én plan i kurven ad
 * gangen, så et nyt valg erstatter det gamle.
 */
export function setPlan( plan ) {
	const cart = readCart();
	cart.plan = { id: plan.id, name: plan.name, price: plan.price };
	return writeCart( cart );
}

/**
 * Tilføjer et tillæg (fx Ekstra hænder). Findes det allerede, og
 * tillægget er antals-baseret, øges antallet i stedet for at duplikere.
 */
export function addAddon( addon ) {
	const cart = readCart();
	const existing = cart.addons.find( ( a ) => a.id === addon.id );

	if ( existing ) {
		if ( addon.allowQty ) {
			existing.qty += 1;
		}
	} else {
		cart.addons.push( {
			id: addon.id,
			name: addon.name,
			price: addon.price,
			allowQty: !! addon.allowQty,
			unitLabel: addon.unitLabel || '',
			qty: 1,
		} );
	}

	return writeCart( cart );
}

export function updateAddonQty( id, qty ) {
	const cart = readCart();
	const item = cart.addons.find( ( a ) => a.id === id );
	if ( ! item ) {
		return cart;
	}

	if ( qty <= 0 ) {
		cart.addons = cart.addons.filter( ( a ) => a.id !== id );
	} else {
		item.qty = qty;
	}

	return writeCart( cart );
}

export function removePlan() {
	const cart = readCart();
	cart.plan = null;
	return writeCart( cart );
}

export function removeAddon( id ) {
	const cart = readCart();
	cart.addons = cart.addons.filter( ( a ) => a.id !== id );
	return writeCart( cart );
}

export function clearCart() {
	return writeCart( emptyCart() );
}

export function getTotal( cart = readCart() ) {
	const planTotal = cart.plan ? cart.plan.price : 0;
	const addonsTotal = cart.addons.reduce( ( sum, a ) => sum + a.price * ( a.qty || 1 ), 0 );
	return planTotal + addonsTotal;
}

export function getItemCount( cart = readCart() ) {
	const planCount = cart.plan ? 1 : 0;
	const addonsCount = cart.addons.reduce( ( sum, a ) => sum + ( a.qty || 1 ), 0 );
	return planCount + addonsCount;
}

export function isEmpty( cart = readCart() ) {
	return ! cart.plan && cart.addons.length === 0;
}

export function formatPrice( n ) {
	return n.toLocaleString( 'da-DK' ) + ' kr/md';
}

/**
 * Ren tekst-opsummering af kurven, til det skjulte felt der sendes
 * med bestillingsformularen.
 */
export function cartSummaryText( cart = readCart() ) {
	if ( isEmpty( cart ) ) {
		return 'Ingen valg i kurven.';
	}

	const lines = [];

	if ( cart.plan ) {
		lines.push( `${ cart.plan.name } — ${ formatPrice( cart.plan.price ) }` );
	}

	cart.addons.forEach( ( a ) => {
		const qtyLabel = a.qty > 1 ? ` × ${ a.qty }` : '';
		lines.push( `${ a.name }${ qtyLabel } — ${ formatPrice( a.price * ( a.qty || 1 ) ) }` );
	} );

	lines.push( `I alt: ${ formatPrice( getTotal( cart ) ) }` );

	return lines.join( '\n' );
}
